import type { DailyReport } from "../../store/useDailyReportStore";
import {
  Target,
  Trophy,
  DollarSign,
  Briefcase,
  Users,
  FileText,
  Zap,
  Clock,
  Wallet,
  Activity,
  TrendingUp,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtTime = (iso: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const Kpi = ({
  label,
  value,
  sub,
  icon: Icon,
  tone = "blue",
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: any;
  tone?: "blue" | "emerald" | "amber" | "violet" | "rose";
}) => {
  const tones: Record<string, string> = {
    blue: "from-blue-50 to-blue-100/40 text-blue-700",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700",
    amber: "from-amber-50 to-amber-100/40 text-amber-700",
    violet: "from-violet-50 to-violet-100/40 text-violet-700",
    rose: "from-rose-50 to-rose-100/40 text-rose-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em]">
          {label}
        </span>
        <div
          className={`w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br ${tones[tone]}`}
        >
          <Icon size={18} />
        </div>
      </div>
      <p className="text-[28px] font-black leading-none tracking-tight text-slate-900">
        {value}
      </p>
      {sub && (
        <p className="text-[11px] text-slate-500 mt-2 font-medium">{sub}</p>
      )}
    </div>
  );
};

const SectionCard = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className="text-blue-600" />
      <h3 className="text-[14px] font-bold text-slate-900">{title}</h3>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{children}</div>
  </div>
);

const StatRow = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) => (
  <div className="bg-slate-50/70 rounded-lg p-3 border border-slate-100">
    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-500">
      {label}
    </p>
    <p
      className={`text-[16px] font-extrabold mt-1 ${accent || "text-slate-900"}`}
    >
      {value}
    </p>
  </div>
);

const DailyReportView = ({
  report,
  publicView = false,
}: {
  report: DailyReport;
  publicView?: boolean;
}) => {
  const bd =
    typeof report.bd === "object" && report.bd
      ? report.bd
      : { _id: "", fullName: "Business Developer", email: "" };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0058c3] to-[#0070f3] rounded-2xl p-6 text-white shadow-[0_8px_30px_rgba(0,88,195,0.25)]">
        <p className="text-[11px] uppercase tracking-[0.15em] font-bold opacity-85">
          Daily Performance Report
        </p>
        <h1 className="text-[24px] sm:text-[28px] font-black mt-1 leading-tight">
          {bd.fullName}
        </h1>
        <p className="text-[13px] opacity-90 mt-1">{fmtDate(report.workDate)}</p>

        <div className="grid grid-cols-3 gap-3 mt-5">
          <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
            <p className="text-[10px] uppercase tracking-wider opacity-85">
              Shift Start
            </p>
            <p className="text-[14px] font-bold mt-0.5">
              {fmtTime(report.shiftStart)}
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
            <p className="text-[10px] uppercase tracking-wider opacity-85">
              Shift End
            </p>
            <p className="text-[14px] font-bold mt-0.5">
              {fmtTime(report.shiftEnd)}
            </p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 backdrop-blur-sm">
            <p className="text-[10px] uppercase tracking-wider opacity-85">
              Hours Worked
            </p>
            <p className="text-[14px] font-bold mt-0.5">
              {report.totalWorkingHours}h
            </p>
          </div>
        </div>
      </div>

      {/* Hero KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          label="Bids Submitted"
          value={report.bidding.totalBids}
          sub={`${report.bidding.wonBids} won · ${report.bidding.successRate}% success`}
          icon={Target}
          tone="amber"
        />
        <Kpi
          label="Leads Generated"
          value={report.leads.totalLeads}
          sub={`${report.leads.qualifiedLeads} qualified · ${report.leads.convertedToOpportunities} converted`}
          icon={Users}
          tone="violet"
        />
        <Kpi
          label="Projects Won"
          value={report.projects.projectsWon}
          sub={`${fmtMoney(report.projects.totalValueAcquired)} acquired`}
          icon={Trophy}
          tone="emerald"
        />
        <Kpi
          label="Confirmed Revenue"
          value={fmtMoney(report.financials.confirmedRevenue)}
          sub={`${fmtMoney(report.financials.pipelineValue)} in pipeline`}
          icon={DollarSign}
          tone="blue"
        />
      </div>

      {/* Bidding detail */}
      <SectionCard title="Bidding Performance" icon={Target}>
        <StatRow label="Total Bids" value={report.bidding.totalBids} />
        <StatRow
          label="Won"
          value={report.bidding.wonBids}
          accent="text-emerald-700"
        />
        <StatRow
          label="Lost"
          value={report.bidding.lostBids}
          accent="text-rose-700"
        />
        <StatRow label="Open" value={report.bidding.openBids} />
        <StatRow
          label="Success Rate"
          value={`${report.bidding.successRate}%`}
        />
        <StatRow
          label="Avg Bid Value"
          value={fmtMoney(report.bidding.avgBidValue)}
        />
      </SectionCard>

      {/* Proposals + Leads */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SectionCard title="Proposal Performance" icon={FileText}>
          <StatRow
            label="Generated"
            value={report.proposals.totalGenerated}
          />
          <StatRow label="Submitted" value={report.proposals.submitted} />
          <StatRow
            label="In Negotiation"
            value={report.proposals.inNegotiation}
          />
        </SectionCard>
        <SectionCard title="Lead Generation" icon={Users}>
          <StatRow label="Total Leads" value={report.leads.totalLeads} />
          <StatRow
            label="Qualified"
            value={report.leads.qualifiedLeads}
            accent="text-blue-700"
          />
          <StatRow
            label="Converted"
            value={report.leads.convertedToOpportunities}
            accent="text-emerald-700"
          />
        </SectionCard>
      </div>

      {/* Projects + Connects */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <SectionCard title="Project Acquisition" icon={Briefcase}>
          <StatRow label="Projects" value={report.projects.projectsWon} />
          <StatRow
            label="Total Value"
            value={fmtMoney(report.projects.totalValueAcquired)}
            accent="text-emerald-700"
          />
          <StatRow
            label="Avg Value"
            value={fmtMoney(report.projects.avgProjectValue)}
          />
          <StatRow
            label="Escrow Secured"
            value={fmtMoney(report.projects.escrowSecured)}
          />
        </SectionCard>
        <SectionCard title="Connect Usage" icon={Zap}>
          <StatRow label="Total Used" value={report.connects.totalUsed} />
          <StatRow
            label="Avg / Bid"
            value={report.connects.avgConnectsPerBid}
          />
          <StatRow
            label="Efficiency"
            value={`${report.connects.efficiencyRatio}%`}
            accent="text-amber-700"
          />
        </SectionCard>
      </div>

      {/* Financial Overview */}
      <SectionCard title="Financial Overview" icon={Wallet}>
        <StatRow
          label="Potential Revenue"
          value={fmtMoney(report.financials.potentialRevenue)}
        />
        <StatRow
          label="Confirmed Revenue"
          value={fmtMoney(report.financials.confirmedRevenue)}
          accent="text-emerald-700"
        />
        <StatRow
          label="Pipeline Value"
          value={fmtMoney(report.financials.pipelineValue)}
          accent="text-blue-700"
        />
      </SectionCard>

      {/* Productivity */}
      <SectionCard title="Productivity Summary" icon={Activity}>
        <StatRow
          label="Questionnaires"
          value={report.productivity.questionnairesGenerated}
        />
        <StatRow
          label="Tasks Completed"
          value={report.productivity.tasksCompleted}
        />
        <StatRow
          label="Activities"
          value={report.productivity.activitiesCount}
        />
      </SectionCard>

      {/* Notes */}
      {report.notes && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText size={16} className="text-blue-600" />
            <h3 className="text-[14px] font-bold text-slate-900">Notes</h3>
          </div>
          <p className="text-[13px] text-slate-700 whitespace-pre-line">
            {report.notes}
          </p>
        </div>
      )}

      {/* Delivery status */}
      {!publicView && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3 text-[12px]">
          <span className="text-slate-500 font-semibold">
            Delivery status:
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold ${
              report.deliveries.emailSent
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {report.deliveries.emailSent ? (
              <CheckCircle2 size={12} />
            ) : (
              <XCircle size={12} />
            )}
            Email
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold ${
              report.deliveries.slackSent
                ? "bg-emerald-50 text-emerald-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {report.deliveries.slackSent ? (
              <CheckCircle2 size={12} />
            ) : (
              <XCircle size={12} />
            )}
            Slack
          </span>
          <span className="text-slate-400 ml-auto inline-flex items-center gap-1">
            <Clock size={12} />
            Generated{" "}
            {new Date(report.createdAt).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      )}

      {publicView && (
        <div className="text-center text-[11px] text-slate-400 py-4">
          <TrendingUp size={12} className="inline mr-1" />
          Powered by Omni Workspace · Daily Performance Reporting
        </div>
      )}
    </div>
  );
};

export default DailyReportView;
